import React, { useEffect, useState } from "react";
import { Gauge, AlertTriangle } from "lucide-react";
import { api } from "../../../lib/api";
import { apiErr } from "../../../lib/apiErr";
import { isCanceled, useLatestRequest } from "../../../lib/useLatest";
import { PageHeader } from "../../../components/PageHeader";
import { fmtWib } from "../signals/signalUtil";

const DAYS = [1, 7, 30];
export const ms = (v) => (v == null ? "—" : v >= 1000 ? `${(v / 1000).toLocaleString("id-ID", { maximumFractionDigits: 2 })} dtk` : `${Math.round(v)} ms`);
const ENV = { preview: "Preview", production: "Produksi" };
const METRICS = [["first_byte", "Byte pertama"], ["first_token", "Kata pertama"], ["total", "Total"]];

function Cards({ a, target }) {
  const slow = a.first_token_p50 != null && a.first_token_p50 > target;
  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {METRICS.map(([k, l]) => (
        <div key={k} className={`aivora-card p-5 ${k === "first_token" && slow ? "ring-2 ring-rose-300" : ""}`} data-testid={`cl-card-${k}`}>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{l}</p>
          <p className={`mt-1 font-mono text-2xl font-bold ${k === "first_token" && slow ? "text-rose-600" : "text-slate-900"}`} data-testid={`cl-card-${k}-p50`}>{ms(a[`${k}_p50`])}</p>
          <p className="text-[11px] text-slate-500">p50 · p95 <b className="font-mono text-slate-700" data-testid={`cl-card-${k}-p95`}>{ms(a[`${k}_p95`])}</b>{k === "first_token" && <> · target ≤ {ms(target)}</>}</p>
        </div>
      ))}
      <div className="aivora-card p-5" data-testid="cl-card-n"><p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Jumlah pesan</p><p className="mt-1 font-mono text-2xl font-bold text-slate-900" data-testid="cl-card-n-value">{(a.n ?? 0).toLocaleString("id-ID")}</p></div>
    </div>
  );
}

function EnvCompare({ by, target }) {
  const envs = ["preview", "production"];
  return (
    <div className="aivora-card overflow-x-auto p-0" data-testid="cl-env">
      <table className="w-full min-w-[520px] text-left text-xs">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500"><tr><th className="px-4 py-2.5">Fase</th>{envs.map((e) => <th key={e} className="px-4 py-2.5 text-right">{ENV[e]} <span className="font-mono normal-case text-slate-400">n={by[e]?.n ?? 0}</span></th>)}</tr></thead>
        <tbody className="divide-y divide-line">
          {METRICS.flatMap(([k, l]) => ["p50", "p95"].map((p) => (
            <tr key={`${k}${p}`}><td className="px-4 py-2 text-slate-700">{l} <span className="font-mono text-slate-400">{p}</span></td>
              {envs.map((e) => { const v = by[e]?.[`${k}_${p}`]; const bad = k === "first_token" && p === "p50" && v != null && v > target; return <td key={e} className={`px-4 py-2 text-right font-mono ${bad ? "font-bold text-rose-600" : "text-slate-800"}`} data-testid={`cl-env-${e}-${k}-${p}`}>{ms(v)}</td>; })}
            </tr>
          )))}
        </tbody>
      </table>
    </div>
  );
}

function Slowest({ rows }) {
  return (
    <div className="aivora-card overflow-x-auto p-0" data-testid="cl-slowest">
      <p className="px-4 pt-4 text-sm font-bold text-slate-900">10 pesan terlambat</p>
      <table className="mt-2 w-full min-w-[760px] text-left text-xs">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500"><tr>{["Waktu (WIB)", "Env", "Byte pertama", "Kata pertama", "Total", "Kanal", "Bertugas", "Alat"].map((h) => <th key={h} className="px-3 py-2.5">{h}</th>)}</tr></thead>
        <tbody className="divide-y divide-line">
          {rows.map((r, i) => (
            <tr key={i} data-testid={`cl-slow-${i}`}>
              <td className="whitespace-nowrap px-3 py-2 text-slate-600">{fmtWib(r.at)}</td>
              <td className="px-3 py-2"><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${r.env === "production" ? "bg-brand-soft text-brand" : "bg-slate-100 text-slate-600"}`}>{ENV[r.env] || r.env}</span></td>
              <td className="px-3 py-2 font-mono">{ms(r.first_byte_ms)}</td><td className="px-3 py-2 font-mono">{ms(r.first_token_ms)}</td><td className="px-3 py-2 font-mono font-bold">{ms(r.total_ms)}</td>
              <td className="px-3 py-2">{r.channel === "call" ? "Panggilan" : r.channel === "chat" ? "Chat" : r.channel || "—"}</td>
              <td className="px-3 py-2">{r.task_signal ? <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">Ya</span> : <span className="text-slate-400">Tidak</span>}</td>
              <td className="px-3 py-2 font-mono text-[11px]">{r.tool || "—"}</td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={8} className="px-4 py-6 text-center text-slate-400" data-testid="cl-slow-empty">Belum ada data.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export default function PlatformChatLatency() {
  const [days, setDays] = useState(7); const [d, setD] = useState(null); const [err, setErr] = useState(""); const next = useLatestRequest();
  useEffect(() => { setD(null); api.get("/admin/chat-latency", { params: { days }, signal: next() }).then((r) => { setD(r.data); setErr(""); }).catch((e) => { if (!isCanceled(e)) setErr(e?.response?.status === 404 ? "Endpoint Kecepatan Chat belum tersedia di server (404)." : apiErr(e, "Gagal memuat latensi")); }); }, [days, next]);
  const target = d?.target_first_token_ms ?? 3000;
  return (
    <div data-testid="chat-latency-page">
      <PageHeader icon={Gauge} title="Kecepatan Chat" description="Latensi per fase pesan chat (tanpa isi pesan, retensi 30 hari): byte pertama, kata pertama, total — preview vs produksi.">
        <div className="seg" data-testid="cl-days">{DAYS.map((x) => <button key={x} onClick={() => setDays(x)} className={`seg-item ${days === x ? "seg-active" : ""}`} data-testid={`cl-days-${x}`}>{x} hari</button>)}</div>
      </PageHeader>
      {err && <p className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700" data-testid="cl-error">{err}</p>}
      {!d && !err && <div className="aivora-card h-40 animate-pulse bg-slate-50" />}
      {d && (
        <div className="space-y-5">
          {d.proxy_holding_stream && <p className="flex items-center gap-2 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800" data-testid="cl-proxy-banner"><AlertTriangle size={16} /> Proxy produksi menahan aliran (byte pertama &gt; 1 detik)</p>}
          <Cards a={d.all || {}} target={target} />
          <EnvCompare by={d.by_env || {}} target={target} />
          <Slowest rows={d.slowest || []} />
        </div>
      )}
    </div>
  );
}
